import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, History, ShieldCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchBanned, unbanUser, type AdminBannedUser } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A8.01 — Aktiv cheklovlar (hozir bloklangan foydalanuvchilar). GET /admin/banned
 * (backend `e876f10`). Sabab endi `users.banned_reason` da. Blokdan chiqarish
 * mavjud `/admin/users/:id/unban` bilan (USERS_BAN huquqi).
 */
export function BannedUsers() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const rowActions = useMemo<RowAction<AdminBannedUser>[]>(() => {
    if (!can(PERMISSIONS.USERS_BAN)) return []
    return [
      {
        id: 'unban',
        label: t.users.unban,
        icon: <ShieldCheck size={13} />,
        onSelect: async (r) => {
          try {
            await unbanUser(r.id)
            toast(t.users.actionDone, { tone: 'success' })
            setReloadKey((n) => n + 1)
          } catch (e) {
            toast(t.users.actionFailed, {
              tone: 'error',
              ...(e instanceof Error && e.message ? { description: e.message } : {}),
            })
          }
        },
      },
    ]
  }, [t, can, toast])

  const columns = useMemo<AdminColumn<AdminBannedUser>[]>(
    () => [
      {
        key: 'username',
        header: t.col.name,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.id}`} className="text-accent hover:underline">
            {r.first_name || (r.username ? `@${r.username}` : `#${r.id}`)}
          </Link>
        ),
      },
      { key: 'banned_reason', header: t.users.bannedReason, type: 'longtext', minWidth: 220 },
      { key: 'banned_at', header: t.users.bannedAt, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchBanned, [])

  return (
    <>
      <PageHeader
        title={t.users.activeRestrictions}
        screenId="A8.01"
        actions={
          <Link
            to="/users/ban-log"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <History size={13} />
            {t.users.banLog}
          </Link>
        }
      />
      <DataTable<AdminBannedUser>
        key={reloadKey}
        screenId="A8.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Ban size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
